/**
 * 事件总线 - 实现发布-订阅模式
 * 用于组件间通信，解耦组件之间的直接依赖
 */

type EventCallback = (...args: any[]) => void;

class EventBus {
  private events: Map<string, EventCallback[]>;

  constructor() {
    this.events = new Map<string, EventCallback[]>();
  }

  /**
   * 订阅事件
   * @param eventName 事件名称
   * @param callback 回调函数
   * @returns 取消订阅的函数
   */
  subscribe(eventName: string, callback: EventCallback): () => void {
    if (!this.events.has(eventName)) {
      this.events.set(eventName, []);
    }

    const callbacks = this.events.get(eventName)!;
    callbacks.push(callback);

    // 返回取消订阅的函数
    return () => {
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    };
  }

  /**
   * 发布事件
   * @param eventName 事件名称
   * @param args 传递给订阅者的参数
   */
  publish(eventName: string, ...args: any[]): void {
    if (!this.events.has(eventName)) {
      return;
    }

    const callbacks = this.events.get(eventName)!;
    callbacks.forEach(callback => {
      try {
        callback(...args);
      } catch (error) {
        console.error(`Error in event handler for ${eventName}:`, error);
      }
    });
  }

  /**
   * 取消订阅指定事件的所有回调
   * @param eventName 事件名称
   */
  unsubscribeAll(eventName: string): void {
    this.events.delete(eventName);
  }

  /**
   * 清空所有事件订阅
   */
  clear(): void {
    this.events.clear();
  }
}

// 创建全局事件总线实例
const eventBus = new EventBus();

// 定义事件名称常量
export const EVENT_NAMES = {
  MAP_LOCATION_CHANGE: 'map_location_change',
  DISTANCE_CHANGE: 'distance_change',
  DRIVER_DATA_UPDATED: 'driver_data_updated', // 司机数据更新事件
  DRIVER_SELECTED: 'driver_selected', // 新增：司机被选中事件
};

export default eventBus;